import * as THREE from 'three'

/** A world-space water volume shared by every shot. The surface and shafts use
 * the authored caustic plate; no camera-facing backdrop or bounded stage edge. */
export class UnderwaterAtmosphere {
  readonly root = new THREE.Group()
  private readonly time = { value: 0 }
  private readonly tint = { value: new THREE.Color(0x277f9b) }
  private readonly light = { value: 1 }
  private baseLight = 1
  private reducedMotion = false

  constructor() {
    this.root.name = 'ContinuousOceanAtmosphere'
    const caustics = new THREE.TextureLoader().load(`${import.meta.env.BASE_URL}textures/aaa/caustics.png`)
    caustics.wrapS = caustics.wrapT = THREE.RepeatWrapping
    const material = new THREE.ShaderMaterial({
      side: THREE.BackSide, depthWrite: false,
      uniforms: { oceanTime: this.time, oceanTint: this.tint, oceanLight: this.light, caustics: { value: caustics } },
      vertexShader: `varying vec3 direction;
        void main() { direction = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
      fragmentShader: `varying vec3 direction;
        uniform float oceanTime; uniform float oceanLight;
        uniform vec3 oceanTint; uniform sampler2D caustics;
        void main() {
          vec3 d = normalize(direction);
          float upper = smoothstep(-0.25,0.85,d.y);
          vec3 color = mix(oceanTint * 0.25, oceanTint * 1.5, upper);
          vec2 uv = d.xz / max(0.18,d.y + 0.3);
          float water = texture2D(caustics,uv * 0.23 + vec2(oceanTime * 0.005,0.0)).r;
          float water2 = texture2D(caustics,uv * 0.31 - vec2(0.0,oceanTime * 0.004)).r;
          color += vec3(0.34,0.7,0.82) * pow(min(water,water2),2.0) * smoothstep(0.1,0.7,d.y) * oceanLight;
          float sun = pow(max(0.0,dot(d,normalize(vec3(-0.3,0.9,-0.35)))),28.0);
          color += vec3(0.5,0.83,0.9) * sun * oceanLight;
          gl_FragColor = vec4(color,1.0);
          #include <tonemapping_fragment>
          #include <colorspace_fragment>
        }`,
    })
    const dome = new THREE.Mesh(new THREE.SphereGeometry(100, 32, 20), material)
    dome.name = 'UnderwaterSurfaceDome'
    dome.renderOrder = -100
    dome.frustumCulled = false
    this.root.add(dome)
    // Shafts live behind the performers, tapering toward the bright surface.
    // Feathering both edges and ends avoids visible translucent cone outlines.
    const shaftMaterial = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
      uniforms: { oceanTime: this.time, oceanLight: this.light },
      vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
      fragmentShader: `varying vec2 vUv; uniform float oceanTime; uniform float oceanLight;
        void main(){ float edge=pow(sin(vUv.x*3.14159265),3.0);
        float ends=smoothstep(0.0,0.2,vUv.y)*(1.0-smoothstep(0.72,1.0,vUv.y));
        float pulse=0.85+0.15*sin(oceanTime*0.2+vUv.y*5.0);
        gl_FragColor=vec4(0.3,0.72,0.85,edge*ends*pulse*0.12*oceanLight); }`,
    })
    for (let i = 0; i < 9; i++) {
      const shaft = new THREE.Mesh(new THREE.PlaneGeometry(1.5 + i % 3, 35), shaftMaterial)
      shaft.position.set(-20 + i * 5.5, 13, -15 - (i % 3) * 7)
      shaft.rotation.z = -0.22
      this.root.add(shaft)
    }
  }

  setColor(color: number, density: number): void {
    this.tint.value.setHex(color).lerp(new THREE.Color(0x278dad), 0.6)
    this.baseLight = density > 0.03 ? 0.42 : 1.0
    this.applyLight()
  }

  setReducedMotion(reduced: boolean): void {
    if (this.reducedMotion === reduced) return
    this.reducedMotion = reduced
    this.applyLight()
  }

  private applyLight(): void {
    this.light.value = this.reducedMotion ? Math.min(this.baseLight, 0.3) : this.baseLight
  }

  update(elapsed: number): void {
    if (this.reducedMotion) return
    this.time.value = elapsed
  }

  dispose(): void {
    const geometries = new Set<THREE.BufferGeometry>()
    const materials = new Set<THREE.Material>()
    const textures = new Set<THREE.Texture>()
    this.root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return
      geometries.add(object.geometry)
      for (const material of Array.isArray(object.material) ? object.material : [object.material]) {
        materials.add(material)
        if (material instanceof THREE.ShaderMaterial && material.uniforms.caustics) {
          textures.add(material.uniforms.caustics.value)
        }
      }
    })
    geometries.forEach(geometry => geometry.dispose())
    materials.forEach(material => material.dispose())
    textures.forEach(texture => texture.dispose())
    this.root.clear()
    this.root.removeFromParent()
  }
}
